/**
 * Unified Analytics Page
 *
 * CDP Phase 3: Single Pane of Glass Analytics
 *
 * Provides unified analytics across all resource types with full hierarchy drill-down:
 * - Workspace overview (all resources)
 * - Resource type filtering (Links, QR, Pages)
 * - Project breakdown with drill-down
 * - Timeline data with resource type stacking
 * - Top performers by resource type
 */

import { useMemo, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useQueryState, parseAsString, parseAsArrayOf } from 'nuqs';
import {
  ChartLine,
  TrendingUp,
  Users,
  MousePointerClick,
  QrCode,
  FileText,
  Activity,
  Layers,
  ArrowUpRight,
  Crown,
  Link2,
  ScanLine,
  Eye,
  Filter,
  Check,
  BarChart2,
  Layers2,
  Compass,
  Globe,
  Laptop,
  Lightbulb,
} from 'lucide-react';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';
import { apiFetch } from '@/client/lib/api';
import { formatNumber, getUTCDateString } from '@/client/lib/utils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Skeleton } from '@/client/components/ui/skeleton';

// ⚡ Performance Optimization: Keep above-the-fold charts as regular imports
// Direct imports instead of barrel file to improve tree-shaking
import { VisitsChart } from '@/client/components/charts/visits-chart';
import { ResourceTypeTimelineChart } from '@/client/components/charts/resource-type-timeline-chart';
import { PeriodComparison } from '@/client/components/charts/period-comparison';
import { CompactChartSkeleton } from '@/client/components/charts/chart-skeleton';

// ⚡ Performance Optimization: Lazy load below-the-fold charts
// These charts appear further down the page and can be loaded on-demand
const DayOfWeekChart = lazy(() =>
  import('@/client/components/charts/day-of-week-chart').then((m) => ({
    default: m.DayOfWeekChart,
  }))
);
const HourOfDayChart = lazy(() =>
  import('@/client/components/charts/hour-of-day-chart').then((m) => ({
    default: m.HourOfDayChart,
  }))
);
const VisitorTypeChart = lazy(() =>
  import('@/client/components/charts/visitor-type-chart').then((m) => ({
    default: m.VisitorTypeChart,
  }))
);
const ReferrerCategoriesChart = lazy(() =>
  import('@/client/components/charts/referrer-categories-chart').then((m) => ({
    default: m.ReferrerCategoriesChart,
  }))
);
const ReferrersChart = lazy(() =>
  import('@/client/components/charts/referrers-chart').then((m) => ({
    default: m.ReferrersChart,
  }))
);
const ReferrerPlatformsChart = lazy(() =>
  import('@/client/components/charts/referrer-platforms-chart').then((m) => ({
    default: m.ReferrerPlatformsChart,
  }))
);
const CountriesChart = lazy(() =>
  import('@/client/components/charts/countries-chart').then((m) => ({
    default: m.CountriesChart,
  }))
);
const CitiesChart = lazy(() =>
  import('@/client/components/charts/cities-chart').then((m) => ({
    default: m.CitiesChart,
  }))
);
const DevicesChart = lazy(() =>
  import('@/client/components/charts/devices-chart').then((m) => ({
    default: m.DevicesChart,
  }))
);
const BrowsersChart = lazy(() =>
  import('@/client/components/charts/browsers-chart').then((m) => ({
    default: m.BrowsersChart,
  }))
);
const OSChart = lazy(() =>
  import('@/client/components/charts/os-chart').then((m) => ({
    default: m.OSChart,
  }))
);
const ClickRecords = lazy(() =>
  import('@/client/components/charts/click-records').then((m) => ({
    default: m.ClickRecords,
  }))
);
const InsightsWizardModal = lazy(() =>
  import('@/client/components/insights/insights-wizard-modal').then((m) => ({
    default: m.InsightsWizardModal,
  }))
);
import {
  DateRangeSelector,
  type DateRangePreset,
} from '@/client/components/ui/date-range-selector';
import { AttributionFilters } from '@/client/components/analytics/attribution-filters';
import { ProjectFilter } from '@/client/components/analytics/project-filter';
import { ResourcePerformanceTable } from '@/client/components/analytics/resource-performance-table';

// =============================================================================
// Types
// =============================================================================

type ResourceType = 'link' | 'qr' | 'page';

interface AnalyticsSummary {
  totalInteractions: number;
  uniqueVisitors: number;
  uniqueSessions: number;
  conversions: number;
  conversionRate: number;
  newVisitors: number;
  returningVisitors: number;
}

interface ResourceTypeBreakdown {
  link: { clicks: number; uniqueVisitors: number };
  qr: { scans: number; uniqueVisitors: number };
  page: { views: number; uniqueVisitors: number };
}

interface ProjectBreakdown {
  projectId: string;
  projectName: string;
  projectColor: string;
  totalInteractions: number;
  conversions: number;
  topResource: {
    type: string;
    id: string;
    name: string;
    count: number;
  } | null;
}

/**
 * Top performer data from the analytics API.
 * Matches the backend's ResourceSummary structure.
 */
interface TopPerformer {
  resourceId: string;
  resourceName: string;
  resourceType: 'link' | 'qr' | 'page';
  interactions: number;
  uniqueVisitors: number;
  conversions: number;
  projectId: string | null;
  projectName: string | null;
}

interface TimelineDataPoint {
  date: string;
  interactions: number;
  uniqueVisitors?: number;
  byResourceType?: {
    link: number;
    qr: number;
    page: number;
  };
}

interface AttributionBreakdown {
  sources: Record<string, number>;
  referrerCategories: Record<string, number>;
  referrerPlatforms: Record<string, number>;
  utmSources: Record<string, number>;
  utmMediums: Record<string, number>;
  utmCampaigns: Record<string, number>;
}

interface ContextBreakdown {
  devices: Record<string, number>;
  browsers: Record<string, number>;
  osTypes: Record<string, number>;
  countries: Record<string, number>;
  cities: Record<string, number>;
}

interface AnalyticsOverviewResponse {
  workspace: string;
  period: string;
  from?: string;
  to?: string;
  summary: AnalyticsSummary;
  byResourceType: ResourceTypeBreakdown;
  byProject: ProjectBreakdown[];
  timeline: TimelineDataPoint[];
  topPerformers: {
    links: TopPerformer[];
    qrCodes: TopPerformer[];
    pages: TopPerformer[];
  };
  attribution: AttributionBreakdown;
  context: ContextBreakdown;
  timeDistribution: {
    hours: Record<string, number>;
    days: Record<string, number>;
  };
}

// =============================================================================
// Resource Type Filter
// =============================================================================

interface ResourceTypeFilterProps {
  selected: ResourceType[];
  onChange: (types: ResourceType[]) => void;
  disabled?: boolean;
}

const RESOURCE_TYPES: { id: ResourceType; label: string; icon: React.ElementType }[] = [
  { id: 'link', label: 'Links', icon: Link2 },
  { id: 'qr', label: 'QR Codes', icon: QrCode },
  { id: 'page', label: 'Pages', icon: FileText },
];

function ResourceTypeFilter({ selected, onChange, disabled }: ResourceTypeFilterProps) {
  // Exclusive selection: clicking a type shows ONLY that type
  const selectType = (type: ResourceType) => {
    if (disabled) return;
    onChange([type]);
  };

  const allSelected = selected.length === RESOURCE_TYPES.length;

  return (
    <div className="flex items-center gap-2">
      <Button
        variant={allSelected ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange(['link', 'qr', 'page'])}
        className="rounded-xl gap-1.5"
        disabled={disabled}
      >
        <Layers className="size-4" />
        All
        {allSelected && <Check className="size-3 ml-1" />}
      </Button>
      {RESOURCE_TYPES.map(({ id, label, icon: Icon }) => {
        const isOnlySelected = selected.length === 1 && selected[0] === id;
        return (
          <Button
            key={id}
            variant={isOnlySelected ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectType(id)}
            className="rounded-xl gap-1.5"
            disabled={disabled}
          >
            <Icon className="size-4" />
            {label}
            {isOnlySelected && <Check className="size-3 ml-1" />}
          </Button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Stats Cards
// =============================================================================

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ElementType;
  gradient: string;
  isLoading?: boolean;
  suffix?: string;
}

function StatCard({ title, value, icon: Icon, gradient, isLoading, suffix }: StatCardProps) {
  return (
    <Card className="rounded-2xl group card-hover py-0">
      <CardContent className="flex items-center gap-4 p-5">
        <div
          className={`size-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}
        >
          <Icon className="size-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-20" />
          ) : (
            <p className="text-3xl font-bold tabular-nums">
              {formatNumber(value)}
              {suffix && <span className="text-lg text-muted-foreground ml-1">{suffix}</span>}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Project Breakdown Card
// =============================================================================

interface ProjectBreakdownCardProps {
  projects: ProjectBreakdown[];
  isLoading?: boolean;
}

function ProjectBreakdownCard({ projects, isLoading }: ProjectBreakdownCardProps) {
  if (isLoading) {
    return (
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            Projects & Campaigns
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
              <Skeleton className="size-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            Projects & Campaigns
          </CardTitle>
          <CardDescription>
            Organize your links into projects to see project-level analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="size-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Layers className="size-8 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground text-sm">No projects with activity yet</p>
            <Button asChild variant="outline" size="sm" className="mt-4 rounded-xl">
              <Link to="/projects">
                Create a Project
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl py-0">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="size-5 text-primary" />
            Projects & Campaigns
          </CardTitle>
          <Button asChild variant="ghost" size="sm" className="rounded-xl">
            <Link to="/projects">
              View All
              <ArrowUpRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {projects.slice(0, 5).map((project) => (
          <Link
            key={project.projectId}
            to={`/projects/${project.projectId}`}
            className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group"
          >
            <div
              className="size-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${project.projectColor}20` }}
            >
              <Layers className="size-5" style={{ color: project.projectColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate group-hover:text-primary transition-colors">
                {project.projectName}
              </p>
              {project.topResource && (
                <p className="text-xs text-muted-foreground truncate">
                  Top: {project.topResource.name} ({formatNumber(project.topResource.count)})
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="font-semibold tabular-nums">
                {formatNumber(project.totalInteractions)}
              </p>
              <p className="text-xs text-muted-foreground">interactions</p>
            </div>
            <ArrowUpRight className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Upgrade Banner
// =============================================================================

interface UpgradeBannerProps {
  feature: string;
  tier: 'plus' | 'pro';
  onUpgradeClick: () => void;
}

function UpgradeBanner({ feature, tier, onUpgradeClick }: UpgradeBannerProps) {
  return (
    <Card className="rounded-2xl border-dashed border-2 border-amber-300 dark:border-amber-800 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20 py-0">
      <CardContent className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="size-5 text-white" />
          </div>
          <div>
            <p className="font-medium">
              {feature} requires {tier === 'plus' ? 'Plus' : 'Pro'}
            </p>
            <p className="text-sm text-muted-foreground">Upgrade to unlock advanced analytics</p>
          </div>
        </div>
        <Button
          onClick={onUpgradeClick}
          className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
        >
          <Crown className="size-4 mr-2" />
          Upgrade
        </Button>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main Analytics Page
// =============================================================================

export function AnalyticsPage() {
  // Feature gates for Plus analytics features
  const {
    hasAccess: hasConversionTracking,
    requiredTier: conversionTier,
    openUpgradeModal: openConversionModal,
    UpgradeModalElement: ConversionUpgradeModal,
  } = useFeatureGate('conversion_tracking', 'Filter analytics by traffic source and campaign');

  const {
    hasAccess: hasExtendedHistory,
    openUpgradeModal: openHistoryModal,
    UpgradeModalElement: HistoryUpgradeModal,
  } = useFeatureGate('extended_analytics_history', 'Access analytics history beyond 30 days');

  const {
    hasAccess: hasProjectAnalytics,
    openUpgradeModal: openProjectModal,
    UpgradeModalElement: ProjectUpgradeModal,
  } = useFeatureGate('project_limits', 'View project-level analytics breakdown');

  // Timeline view state
  const [timelineView, setTimelineView] = useState<'combined' | 'byType'>('combined');

  // Insights wizard state
  const [isInsightsOpen, setInsightsOpen] = useState(false);

  // URL-synced state
  const [dateRangePreset, setDateRangePreset] = useQueryState(
    'range',
    parseAsString.withDefault('30d')
  );
  const [urlStartDate, setUrlStartDate] = useQueryState('from', parseAsString);
  const [urlEndDate, setUrlEndDate] = useQueryState('to', parseAsString);
  const [resourceTypesParam, setResourceTypesParam] = useQueryState(
    'types',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // Attribution filter state (URL-synced)
  const [sourcesParam, setSourcesParam] = useQueryState(
    'sources',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const [categoriesParam, setCategoriesParam] = useQueryState(
    'categories',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const [campaignsParam, setCampaignsParam] = useQueryState(
    'campaigns',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // ID filters (URL-synced, primarily for deep links)
  const [projectIdsParam, setProjectIdsParam] = useQueryState(
    'projectIds',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const [resourceIdsParam] = useQueryState(
    'resourceIds',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // Parse resource types
  const selectedResourceTypes: ResourceType[] = useMemo(() => {
    if (resourceTypesParam.length === 0) {
      return ['link', 'qr', 'page'];
    }
    return resourceTypesParam.filter((t): t is ResourceType => ['link', 'qr', 'page'].includes(t));
  }, [resourceTypesParam]);

  // Calculate dates - maps preset to days (hour presets use fraction of days)
  const presetDays: Record<string, number> = {
    '1h': 1, // 1 hour = same day
    '24h': 1, // 24 hours = today/yesterday
    '7d': 7,
    '14d': 14,
    '30d': 30,
    '90d': 90,
    '365d': 365,
  };

  const isCustomRange = dateRangePreset === 'custom' && urlStartDate && urlEndDate;
  const limitDays = presetDays[dateRangePreset] ?? 30;

  const displayDates = useMemo(() => {
    if (isCustomRange && urlStartDate && urlEndDate) {
      return { startDate: urlStartDate, endDate: urlEndDate };
    }
    // Use UTC dates to match DateRangeSelector which uses toISOString()
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - limitDays + 1);
    return { startDate: getUTCDateString(start), endDate: getUTCDateString(today) };
  }, [isCustomRange, urlStartDate, urlEndDate, limitDays]);

  const { startDate, endDate } = displayDates;

  // Handlers
  const handleDateRangeChange = (preset: DateRangePreset, start?: string, end?: string) => {
    setDateRangePreset(preset);
    if (preset === 'custom' && start && end) {
      setUrlStartDate(start);
      setUrlEndDate(end);
    } else {
      setUrlStartDate(null);
      setUrlEndDate(null);
    }
  };

  const handleResourceTypesChange = (types: ResourceType[]) => {
    if (types.length === 3) {
      setResourceTypesParam(null);
    } else {
      setResourceTypesParam(types);
    }
  };

  const handleProjectFilterChange = (ids: string[]) => {
    setProjectIdsParam(ids.length > 0 ? ids : null);
  };

  // Clear all attribution filters
  const handleClearAttributionFilters = () => {
    setSourcesParam(null);
    setCategoriesParam(null);
    setCampaignsParam(null);
  };

  // Fetch analytics overview
  const { data: overview, isLoading } = useQuery<AnalyticsOverviewResponse>({
    queryKey: [
      'analytics',
      'overview',
      dateRangePreset,
      startDate,
      endDate,
      selectedResourceTypes,
      sourcesParam,
      categoriesParam,
      campaignsParam,
      projectIdsParam,
      resourceIdsParam,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('period', dateRangePreset);
      if (isCustomRange) {
        params.set('from', startDate);
        params.set('to', endDate);
      }
      if (selectedResourceTypes.length > 0 && selectedResourceTypes.length < 3) {
        params.set('resourceTypes', selectedResourceTypes.join(','));
      }
      // ID filters
      if (projectIdsParam.length > 0) {
        params.set('projectIds', projectIdsParam.join(','));
      }
      if (resourceIdsParam.length > 0) {
        params.set('resourceIds', resourceIdsParam.join(',')); // Note: API might expect 'resourceIds' or handle per type?
        // workers/src/api/analytics.ts does NOT seem to have 'resourceIds' in analyticsQuerySchema!
        // Let me re-check workers/src/api/analytics.ts
      }
      // Attribution filters
      if (sourcesParam.length > 0) {
        params.set('sources', sourcesParam.join(','));
      }
      if (categoriesParam.length > 0) {
        params.set('referrerCategories', categoriesParam.join(','));
      }
      if (campaignsParam.length > 0) {
        params.set('utmCampaigns', campaignsParam.join(','));
      }
      const response = await apiFetch(`/api/analytics/overview?${params}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch analytics');
      }
      return response.json();
    },
  });

  // Transform timeline data for chart
  const chartData = useMemo(() => {
    if (!overview?.timeline) return [];
    return overview.timeline.map((point) => ({
      date: point.date,
      interactions: point.interactions,
      uniqueVisitors: point.uniqueVisitors || 0,
      byResourceType: point.byResourceType,
    }));
  }, [overview?.timeline]);

  // Derive filter options from attribution data
  const filterOptions = useMemo(() => {
    const sources = overview?.attribution?.sources
      ? Object.entries(overview.attribution.sources)
          .map(([value, count]) => ({ value, label: value || 'Direct', count }))
          .sort((a, b) => b.count - a.count)
      : [];

    const categories = overview?.attribution?.referrerCategories
      ? Object.entries(overview.attribution.referrerCategories)
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => b.count - a.count)
      : [];

    const campaigns = overview?.attribution?.utmCampaigns
      ? Object.entries(overview.attribution.utmCampaigns)
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => b.count - a.count)
      : [];

    return { sources, categories, campaigns };
  }, [overview?.attribution]);

  // Check tier access for features
  const canAccessResourceFiltering = hasConversionTracking;
  const canAccessExtendedHistory = hasExtendedHistory;
  const historyLimitExceeded = !canAccessExtendedHistory && (limitDays > 7 || isCustomRange);

  return (
    <div className="space-y-8 stagger-children">
      {/* ... (Header and filters remain same) ... */}
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="size-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <ChartLine className="size-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics</h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              Unified insights across all your assets
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setInsightsOpen(true)}>
            <Lightbulb className="size-4" />
            <span className="hidden sm:inline">Get Insights</span>
          </Button>
          <DateRangeSelector
            value={dateRangePreset as DateRangePreset}
            onChange={handleDateRangeChange}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      </div>

      {/* Resource & Project Filters - Plus Feature */}
      <Card className="rounded-2xl py-0">
        <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-3">
            <Filter className="size-5 text-muted-foreground" />
            <span className="text-sm font-medium">Filter data</span>
            {!canAccessResourceFiltering && (
              <Badge variant="primary-subtle" className="gap-1">
                <Crown className="size-3" />
                Plus
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ResourceTypeFilter
              selected={selectedResourceTypes}
              onChange={handleResourceTypesChange}
              disabled={!canAccessResourceFiltering}
            />
            <ProjectFilter
              selectedProjectIds={projectIdsParam}
              onChange={handleProjectFilterChange}
              disabled={!canAccessResourceFiltering}
            />
          </div>
        </CardContent>
      </Card>

      {/* Attribution Filters - Plus Feature */}
      {(filterOptions.sources.length > 0 ||
        filterOptions.categories.length > 0 ||
        filterOptions.campaigns.length > 0) && (
        <Card className="rounded-2xl py-0">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4">
            <div className="flex items-center gap-3">
              <Compass className="size-5 text-muted-foreground" />
              <span className="text-sm font-medium">Filter by traffic source</span>
              {!hasConversionTracking && <TierBadge tier={conversionTier} size="sm" />}
            </div>
            {hasConversionTracking ? (
              <AttributionFilters
                sources={filterOptions.sources}
                referrerCategories={filterOptions.categories}
                utmCampaigns={filterOptions.campaigns}
                selectedSources={sourcesParam}
                selectedCategories={categoriesParam}
                selectedCampaigns={campaignsParam}
                onSourcesChange={(sources) => setSourcesParam(sources.length > 0 ? sources : null)}
                onCategoriesChange={(categories) =>
                  setCategoriesParam(categories.length > 0 ? categories : null)
                }
                onCampaignsChange={(campaigns) =>
                  setCampaignsParam(campaigns.length > 0 ? campaigns : null)
                }
                onClearAll={handleClearAttributionFilters}
              />
            ) : (
              <Button variant="outline" size="sm" onClick={openConversionModal} className="gap-2">
                <Filter className="size-4" />
                Unlock Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Limit Warning */}
      {historyLimitExceeded && (
        <UpgradeBanner
          feature="Extended analytics history (30+ days)"
          tier="plus"
          onUpgradeClick={openHistoryModal}
        />
      )}

      {/* Summary Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Interactions"
          value={overview?.summary?.totalInteractions ?? 0}
          icon={Activity}
          gradient="from-blue-500 to-indigo-600"
          isLoading={isLoading}
        />
        <StatCard
          title="Unique Visitors"
          value={overview?.summary?.uniqueVisitors ?? 0}
          icon={Users}
          gradient="from-emerald-500 to-teal-600"
          isLoading={isLoading}
        />
        <StatCard
          title="Conversions"
          value={overview?.summary?.conversions ?? 0}
          icon={TrendingUp}
          gradient="from-purple-500 to-pink-600"
          isLoading={isLoading}
        />
        <StatCard
          title="Conversion Rate"
          value={Math.round((overview?.summary?.conversionRate ?? 0) * 100)}
          icon={TrendingUp}
          gradient="from-amber-500 to-orange-600"
          isLoading={isLoading}
          suffix="%"
        />
      </div>

      {/* Resource Type Breakdown */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="rounded-2xl py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center">
              <MousePointerClick className="size-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Link Clicks</p>
              {isLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {formatNumber(overview?.byResourceType?.link?.clicks ?? 0)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
              <ScanLine className="size-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">QR Scans</p>
              {isLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {formatNumber(overview?.byResourceType?.qr?.scans ?? 0)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
              <Eye className="size-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Page Views</p>
              {isLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {formatNumber(overview?.byResourceType?.page?.views ?? 0)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline Chart */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="size-5 text-primary" />
              Activity Over Time
            </CardTitle>
            {/* View Toggle */}
            <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg">
              <Button
                variant={timelineView === 'combined' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimelineView('combined')}
                className="rounded-md gap-1.5 h-7 px-2.5"
              >
                <BarChart2 className="size-3.5" />
                Combined
              </Button>
              <Button
                variant={timelineView === 'byType' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimelineView('byType')}
                className="rounded-md gap-1.5 h-7 px-2.5"
              >
                <Layers2 className="size-3.5" />
                By Type
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Skeleton className="h-full w-full" />
            </div>
          ) : chartData.length > 0 ? (
            <div className="rounded-xl border bg-muted/20 p-4">
              {timelineView === 'combined' ? (
                <VisitsChart data={chartData} days={limitDays} />
              ) : (
                <ResourceTypeTimelineChart timeline={overview?.timeline ?? []} days={limitDays} />
              )}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
              No activity data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Analysis */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="size-5 text-primary" />
            Time Analysis
          </CardTitle>
          <CardDescription>When your audience is most active</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Day of Week - narrower column */}
            <div className="lg:col-span-2 rounded-xl border bg-muted/20 p-4">
              <Suspense fallback={<CompactChartSkeleton />}>
                <DayOfWeekChart data={chartData} />
              </Suspense>
            </div>
            {/* Hour of Day - wider column for time period cards + bar chart */}
            <div className="lg:col-span-3 rounded-xl border bg-muted/20 p-4">
              <Suspense fallback={<CompactChartSkeleton />}>
                <HourOfDayChart data={overview?.timeDistribution?.hours || {}} />
              </Suspense>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Comparison */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Performance Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border bg-muted/20 p-4">
            <PeriodComparison data={chartData} />
          </div>
        </CardContent>
      </Card>

      {/* Project Breakdown - Plus Feature */}
      {!hasProjectAnalytics ? (
        <UpgradeBanner
          feature="Project & Campaign analytics"
          tier="plus"
          onUpgradeClick={openProjectModal}
        />
      ) : (
        <ProjectBreakdownCard projects={overview?.byProject ?? []} isLoading={isLoading} />
      )}

      {/* Attribution Insights */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Compass className="size-5 text-primary" />
            Traffic Sources & Audience
          </CardTitle>
          <CardDescription>Where your visitors are coming from</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <VisitorTypeChart
                    newVisitors={overview?.summary?.newVisitors || 0}
                    returningVisitors={overview?.summary?.returningVisitors || 0}
                  />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <ReferrerCategoriesChart
                    data={overview?.attribution?.referrerCategories || {}}
                    limit={6}
                  />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <ReferrersChart data={overview?.attribution?.sources || {}} limit={6} />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <ReferrerPlatformsChart
                    data={overview?.attribution?.referrerPlatforms || {}}
                    limit={6}
                  />
                </Suspense>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Geographic Distribution */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="size-5 text-primary" />
            Geographic Distribution
          </CardTitle>
          <CardDescription>Where your audience is located</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid md:grid-cols-2 gap-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <CountriesChart data={overview?.context?.countries || {}} />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <CitiesChart data={overview?.context?.cities || {}} />
                </Suspense>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device, Browser & OS Context */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Laptop className="size-5 text-primary" />
            Devices, Browsers & OS
          </CardTitle>
          <CardDescription>Technical context of your visitors</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : Object.keys(overview?.context?.devices || {}).length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <DevicesChart data={overview?.context?.devices || {}} />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <BrowsersChart data={overview?.context?.browsers || {}} />
                </Suspense>
              </div>
              <div className="rounded-xl border bg-muted/20 p-4 md:col-span-2 lg:col-span-1">
                <Suspense fallback={<CompactChartSkeleton />}>
                  <OSChart data={overview?.context?.osTypes || {}} />
                </Suspense>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
              No device/browser data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resource Performance Table */}
      <ResourcePerformanceTable
        links={overview?.topPerformers?.links ?? []}
        qrCodes={overview?.topPerformers?.qrCodes ?? []}
        pages={overview?.topPerformers?.pages ?? []}
      />

      {/* Live Feed */}
      <Card className="rounded-2xl py-0">
        <CardContent className="p-6">
          <Suspense fallback={<CompactChartSkeleton />}>
            <ClickRecords />
          </Suspense>
        </CardContent>
      </Card>

      {/* Insights Wizard Modal */}
      <Suspense fallback={null}>
        <InsightsWizardModal isOpen={isInsightsOpen} onClose={() => setInsightsOpen(false)} />
      </Suspense>

      {/* Upgrade Modals */}
      {ConversionUpgradeModal}
      {HistoryUpgradeModal}
      {ProjectUpgradeModal}
    </div>
  );
}
